import React, { useState, useEffect, useRef } from 'react';
import { Wand2, Image as ImageIcon, ChevronDown, Plus, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translateFieldName, translateOptionValue, translateCommon, hasTranslation } from '../translations';

/**
 * Динамический компонент для отрисовки полей формы на основе input_parameters модели
 */
function DynamicFormField({ 
  fieldName, 
  fieldConfig, 
  value, 
  onChange, 
  disabled = false,
  onValidationChange = null
}) {
  // Состояние для ошибки валидации (должен вызываться всегда, до ранних return)
  const [validationError, setValidationError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Закрытие дропдауна при клике вне его области
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  // Получаем текущий язык
  const { language } = useLanguage();
  
  // Валидация для числовых полей с диапазоном и для строковых с maxLength (должен вызываться всегда, до ранних return)
  useEffect(() => {
    if (!fieldConfig) return;
    
    const { type, min, max, maxLength, maxLengthMessage, default: defaultValue } = fieldConfig;
    
    // Строковое поле с maxLength (из БД, например Kling 3.0 — не больше 2500 знаков)
    if (type === 'string' && maxLength != null) {
      const currentValue = value !== undefined && value !== null ? String(value) : '';
      const error = currentValue.length > maxLength
        ? (maxLengthMessage || `Максимум ${maxLength} символов`)
        : null;
      if (validationError !== error) {
        setValidationError(error);
        if (onValidationChange) onValidationChange(fieldName, error);
      }
      return;
    }
    
    if ((type === 'integer' || type === 'number') && (min !== undefined || max !== undefined)) {
      const currentValue = value !== undefined ? value : (defaultValue !== undefined ? defaultValue : null);
      
      if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
        const numValue = type === 'integer' ? parseInt(currentValue) : parseFloat(currentValue);
        
        if (!isNaN(numValue)) {
          let error = null;
          if (min !== undefined && numValue < min) {
            error = `${translateCommon('min_value', language)}: ${min}`;
          } else if (max !== undefined && numValue > max) {
            error = `${translateCommon('max_value', language)}: ${max}`;
          }
          
          // Обновляем состояние только если ошибка изменилась
          if (validationError !== error) {
            setValidationError(error);
            
            // Уведомляем родительский компонент об ошибке валидации только если она изменилась
            if (onValidationChange) {
              onValidationChange(fieldName, error);
            }
          }
        } else {
          if (validationError !== null) {
            setValidationError(null);
            if (onValidationChange) {
              onValidationChange(fieldName, null);
            }
          }
        }
      } else {
        if (validationError !== null) {
          setValidationError(null);
          if (onValidationChange) {
            onValidationChange(fieldName, null);
          }
        }
      }
    } else {
      if (validationError !== null) {
        setValidationError(null);
        if (onValidationChange) {
          onValidationChange(fieldName, null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldConfig, value, fieldName]);
  
  // Проверка на undefined/null (после всех хуков)
  if (!fieldConfig) {
    console.warn(`DynamicFormField: fieldConfig is undefined for field "${fieldName}"`);
    return null;
  }
  if (fieldConfig.hidden) {
    return null;
  }
  
  const { type, required, description, name, enum: enumValues, min, max, step, maxLength, maxLengthMessage, default: defaultValue, file } = fieldConfig;
  const numericStep = step != null && step !== '' ? Number(step) : (type === 'integer' ? 1 : null);
  const inputStep = numericStep != null && !Number.isNaN(numericStep) ? numericStep : 'any';
  const snapToStep = (n) => {
    if (typeof inputStep !== 'number' || !inputStep) return n;
    const decimals = (String(inputStep).split('.')[1] || '').length;
    return Number((Math.round(n / inputStep) * inputStep).toFixed(decimals));
  };

  // Получаем переведенное название поля
  // Приоритет: перевод из справочника > name из конфига (если не совпадает с fieldName) > fieldName
  let displayName;
  if (hasTranslation(fieldName, language)) {
    // Если есть перевод в справочнике, используем его
    displayName = translateFieldName(fieldName, language, fieldName);
  } else if (name && name !== fieldName) {
    // Если name из БД существует и не равно fieldName (уже переведено в БД), используем его
    displayName = name;
  } else {
    // Используем name из БД, если есть, иначе fieldName
    displayName = name || fieldName;
  }

  // Локализация подписей опций
  const getOptionLabel = (optionFieldName, option) => {
    const labels = fieldConfig.enum_labels || fieldConfig.enumLabels;
    if (labels && typeof labels === 'object') {
      const labeled = labels[option] ?? labels[String(option)];
      if (labeled) return labeled;
    }
    return translateOptionValue(optionFieldName, option, language);
  };

  if (type === 'dialogue') {
    const voices = Array.isArray(fieldConfig.voices) ? fieldConfig.voices : [];
    const fallbackVoice = voices[0]?.value || '';
    const lines = Array.isArray(value) && value.length > 0
      ? value
      : [
          { text: '', voice: fallbackVoice },
          { text: '', voice: voices[1]?.value || fallbackVoice },
        ];
    const maxLength = fieldConfig.maxLength || 5000;
    const used = lines.reduce((sum, line) => sum + String(line?.text || '').length, 0);
    const updateLine = (index, patch) => {
      onChange(lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)));
    };
    return (
      <div className="form-group dialogue-editor">
        <label>
          <span className="field-name">{displayName}</span>
          {required && <span className="required">*</span>}
        </label>
        {description && <div className="field-description">{description}</div>}
        {lines.map((line, index) => (
          <div className="dialogue-line" key={`line-${index}`}>
            <div className="dialogue-line-head">
              <span>Реплика {index + 1}</span>
              {lines.length > 1 && (
                <button
                  type="button"
                  className="dialogue-remove"
                  onClick={() => onChange(lines.filter((_, lineIndex) => lineIndex !== index))}
                  aria-label="Удалить реплику"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <select
              className="form-select"
              value={line.voice || fallbackVoice}
              onChange={(event) => updateLine(index, { voice: event.target.value })}
              disabled={disabled}
            >
              {voices.map((voice) => (
                <option key={voice.value} value={voice.value}>{voice.label || voice.value}</option>
              ))}
            </select>
            <textarea
              className="audio-textarea"
              value={line.text || ''}
              placeholder="Текст реплики"
              rows={3}
              disabled={disabled}
              onChange={(event) => updateLine(index, { text: event.target.value })}
            />
          </div>
        ))}
        <div className="dialogue-footer">
          <span className={`field-hint ${used > maxLength ? 'field-error' : ''}`}>{used} / {maxLength}</span>
          {lines.length < 8 && (
            <button
              type="button"
              className="dialogue-add"
              onClick={() => onChange([...lines, { text: '', voice: voices[lines.length % Math.max(voices.length, 1)]?.value || fallbackVoice }])}
            >
              <Plus size={14} /> Реплика
            </button>
          )}
        </div>
      </div>
    );
  }

  // Специальный рендеринг для aspect_ratio (визуальный выбор)
  if (fieldName === 'aspect_ratio' && enumValues && Array.isArray(enumValues)) {
    const currentValue = value !== undefined ? value : (defaultValue !== undefined ? defaultValue : enumValues[0]);
    
    // Вспомогательная функция для расчета размеров иконки
    const getAspectRatioDimensions = (ratioString) => {
      let w = 1, h = 1;
      if (!ratioString || ratioString === 'auto' || ratioString === 'match_input_image') {
        return { isSpecial: true };
      }
      
      const match = ratioString.match(/(\d+)[:/x](\d+)/);
      if (match) {
        w = parseInt(match[1], 10);
        h = parseInt(match[2], 10);
      } else if (ratioString.includes('portrait')) {
        w = 3; h = 4;
      } else if (ratioString.includes('landscape')) {
        w = 4; h = 3;
      } else if (ratioString.includes('square')) {
        w = 1; h = 1;
      } else {
        w = 1; h = 1;
      }
      
      const maxDim = 20;
      let renderW = w;
      let renderH = h;
      if (w > h) {
        renderW = maxDim;
        renderH = (h / w) * maxDim;
      } else {
        renderH = maxDim;
        renderW = (w / h) * maxDim;
      }
      
      renderW = Math.max(8, renderW);
      renderH = Math.max(8, renderH);
      
      return { w, h, renderW, renderH, isSpecial: false };
    };

    const currentOptionLabel = getOptionLabel(fieldName, currentValue);
    const hasTranslationStrCurrent = currentOptionLabel !== currentValue;
    const currentDims = getAspectRatioDimensions(currentValue);

    return (
      <div className="form-group" ref={dropdownRef}>
        <label htmlFor={fieldName}>
          <span className="field-name">{displayName}</span>
          {required && <span className="required">*</span>}
        </label>
        
        <div className="aspect-ratio-dropdown-container">
          <div 
            className={`aspect-ratio-dropdown-header ${isDropdownOpen ? 'open' : ''}`}
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className="aspect-ratio-header-content">
              <div className="aspect-ratio-icon-container">
                {currentDims.isSpecial ? (
                  currentValue === 'auto' 
                    ? <Wand2 className="aspect-ratio-special-icon" />
                    : <ImageIcon className="aspect-ratio-special-icon" />
                ) : (
                  <div 
                    className="aspect-ratio-rect" 
                    style={{ width: `${currentDims.renderW}px`, height: `${currentDims.renderH}px` }}
                  />
                )}
              </div>
              <div className="aspect-ratio-content">
                <span className="aspect-ratio-value">{currentValue}</span>
                {hasTranslationStrCurrent && (
                  <span className="aspect-ratio-label" title={currentOptionLabel}>{currentOptionLabel}</span>
                )}
              </div>
            </div>
            <ChevronDown className={`aspect-ratio-dropdown-icon ${isDropdownOpen ? 'open' : ''}`} size={20} />
          </div>
          
          <div className={`aspect-ratio-dropdown-list ${isDropdownOpen ? 'open' : ''}`}>
            {enumValues.map((option) => {
              const dims = getAspectRatioDimensions(option);
              const isSelected = currentValue === option;
              const label = getOptionLabel(fieldName, option);
              // Если label совпадает с option, значит перевода нет
              const hasTranslationStr = label !== option;
              
              return (
                <div 
                  key={option} 
                  className={`aspect-ratio-dropdown-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(option);
                    setIsDropdownOpen(false);
                  }}
                >
                  <div className="aspect-ratio-icon-container">
                    {dims.isSpecial ? (
                      option === 'auto' 
                        ? <Wand2 className="aspect-ratio-special-icon" />
                        : <ImageIcon className="aspect-ratio-special-icon" />
                    ) : (
                      <div 
                        className="aspect-ratio-rect" 
                        style={{ width: `${dims.renderW}px`, height: `${dims.renderH}px` }}
                      />
                    )}
                  </div>
                  <div className="aspect-ratio-content">
                    <span className="aspect-ratio-value">{option}</span>
                    {hasTranslationStr && (
                      <span className="aspect-ratio-label" title={label}>{label}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {description && (
          <div className="field-description">{description}</div>
        )}
      </div>
    );
  }

  // String или Integer с enum → Select
  if ((type === 'string' || type === 'integer' || type === 'number') && enumValues && Array.isArray(enumValues)) {
    return (
      <div className="form-group">
        <label htmlFor={fieldName}>
          <span className="field-name">{displayName}</span>
          {required && <span className="required">*</span>}
        </label>
        <select
          id={fieldName}
          value={String(value !== undefined && value !== null ? value : (defaultValue !== undefined ? defaultValue : enumValues[0]))}
            onChange={(e) => {
            const newValue = type === 'integer' || type === 'number' 
              ? (type === 'integer' ? parseInt(e.target.value) : parseFloat(e.target.value))
              : e.target.value;
            onChange(newValue);
          }}
          className="form-select"
          required={required}
          disabled={disabled}
        >
          {enumValues.map((option) => (
            <option key={option} value={option}>
              {getOptionLabel(fieldName, option)}
            </option>
          ))}
        </select>
        {description && (
          <div className="field-description">{description}</div>
        )}
      </div>
    );
  }

  // Integer → Number input с range slider, если есть min и max
  if (type === 'integer' || type === 'number') {
    const hasRange = min !== undefined && max !== undefined;
    // Приоритет: value (из dynamicParams/БД) > defaultValue (из config)
    let defaultVal;
    if (value !== undefined && value !== null && value !== '') {
      // value явно задан (из БД или пользовательского ввода)
      defaultVal = value;
    } else if (defaultValue !== undefined && defaultValue !== null) {
      // Используем defaultValue из config (Replicate)
      defaultVal = defaultValue;
    } else {
      // Fallback на min или 0
      defaultVal = min !== undefined ? min : 0;
    }
    
    // Для seed: если defaultVal 0 или undefined, используем -1
    if (fieldName === 'seed' && (defaultVal === 0 || defaultVal === undefined || defaultVal === null)) {
      defaultVal = -1;
    }
    
    const currentValue = defaultVal;
    
    const handleInputChange = (e) => {
      const inputValue = e.target.value;
      
      // Разрешаем пустое значение для ввода
      if (inputValue === '') {
        onChange('');
        return;
      }
      
      let newValue = type === 'integer' ? parseInt(inputValue) : parseFloat(inputValue);
      
      // Если не число, не меняем значение (позволяем пользователю вводить)
      if (isNaN(newValue)) {
        return;
      }
      
      // НЕ ограничиваем значение автоматически - показываем ошибку валидации
      onChange(newValue);
    };
    
    const handleRangeChange = (e) => {
      const parsed = type === 'integer' ? parseInt(e.target.value) : parseFloat(e.target.value);
      // Ползунок всегда в пределах диапазона
      onChange(snapToStep(parsed));
    };
    
    const handleBlur = () => {
      // При потере фокуса, если значение вне диапазона, не исправляем автоматически
      // Ошибка валидации уже показана через useEffect
    };
    
    if (hasRange) {
      return (
        <div className="form-group">
          <label htmlFor={fieldName}>
            <span className="field-name">{displayName}</span>
            {required && <span className="required">*</span>}
          </label>
          <div className="range-input-container">
            <input
              type="number"
              id={fieldName}
              value={currentValue === '' ? '' : currentValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              step={inputStep}
              required={required}
              disabled={disabled}
              className={`range-number-input ${validationError ? 'input-error' : ''}`}
            />
            <input
              type="range"
              id={`${fieldName}-range`}
              value={currentValue === '' ? (min || 0) : Math.max(min || 0, Math.min(max || 100, currentValue))}
              onChange={handleRangeChange}
              min={min}
              max={max}
              step={inputStep}
              disabled={disabled}
              className="range-slider"
            />
          </div>
          {validationError ? (
            <span className="field-error">
              {validationError}
            </span>
          ) : (
            <span className="field-hint">
              Диапазон: {min} - {max}
            </span>
          )}
        </div>
      );
    }
    
    // Если нет диапазона, используем обычный input
    return (
      <div className="form-group">
        <label htmlFor={fieldName}>
          <span className="field-name">{displayName}</span>
          {required && <span className="required">*</span>}
        </label>
        <input
          type="number"
          id={fieldName}
          value={currentValue === '' ? '' : currentValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          step={inputStep}
          required={required}
          disabled={disabled}
          className={validationError ? 'input-error' : ''}
          placeholder={`${min !== undefined ? min : 0} - ${max !== undefined ? max : '∞'}`}
        />
        {validationError ? (
          <span className="field-error">
            {validationError}
          </span>
        ) : (min !== undefined || max !== undefined) ? (
          <span className="field-hint">
            Диапазон: {min !== undefined ? min : 0} - {max !== undefined ? max : '∞'}
          </span>
        ) : null}
        {description && (
          <div className="field-description">{description}</div>
        )}
      </div>
    );
  }

  // Boolean → Checkbox
  if (type === 'boolean') {
    // Для boolean полей по умолчанию false (галочки сняты)
    const checkboxValue = value !== undefined ? value : (defaultValue !== undefined ? defaultValue : false);
    return (
      <div className="form-group checkbox-group">
        <label htmlFor={fieldName} className="checkbox-label">
          <input
            type="checkbox"
            id={fieldName}
            checked={checkboxValue}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
          <span className="checkbox-text">
            <span className="field-name">{displayName}</span>
          </span>
        </label>
        {description && (
          <div className="field-description">{description}</div>
        )}
      </div>
    );
  }

  // String с file: true → пропускаем (обрабатывается через красивый интерфейс загрузки в ModelPlayground)
  // Поля input_image, image, image_input обрабатываются отдельно
  if (type === 'string' && file === true) {
    return null;
  }

  // String (обычный) → Input (с опциональной валидацией maxLength из БД)
  if (type === 'string') {
    const currentVal = value || defaultValue || '';
    const isTextarea = fieldConfig.widget === 'textarea';
    return (
      <div className="form-group">
        <label htmlFor={fieldName}>
          <span className="field-name">{displayName}</span>
          {required && <span className="required">*</span>}
        </label>
        {isTextarea ? (
          <textarea
            id={fieldName}
            value={currentVal}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            disabled={disabled}
            placeholder={description || ''}
            rows={5}
            className={`audio-textarea ${validationError ? 'input-error' : ''}`}
          />
        ) : (
        <input
          type="text"
          id={fieldName}
          value={currentVal}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={disabled}
          placeholder={description || ''}
          className={validationError ? 'input-error' : ''}
        />
        )}
        {maxLength != null && (
          <span className="field-hint">
            {currentVal.length} / {maxLength}
          </span>
        )}
        {validationError ? (
          <span className="field-error">
            {validationError}
          </span>
        ) : null}
        {description && !validationError && (
          <div className="field-description">{description}</div>
        )}
      </div>
    );
  }

  // Неизвестный тип - пропускаем
  return null;
}

export default DynamicFormField;

